/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Page,
  PageBody,
  Result,
  Stack,
} from '@pypestream/design-system';
import { FC, useEffect } from 'react';
import {
  ErrorBoundary as ReactErrorBoundary,
  useErrorBoundary,
} from 'react-error-boundary';
import * as Sentry from '@sentry/react';
import { isProduction } from '@pypestream/utils';

import { useManagerCtxSelector } from '../../xstate/app.xstate';
import { formatErrorMessage, logError } from './helpers';
import { useErrorTag } from './hooks';
import { ErrorPageProps, ChildrenProps, ErrorTags } from './types';

export const FormattedErrorMessage: FC<{ message: string }> = ({ message }) => {
  const { msg, jsonMsg, query } = formatErrorMessage(message);

  const jsonMessage = jsonMsg ? (
    <>
      <br />
      <br />
      {jsonMsg}
    </>
  ) : null;

  const gqlQuery = query ? (
    <>
      <br />
      <br />
      {query}
    </>
  ) : null;

  return (
    <pre
      style={{
        color: 'white',
        padding: '20px',
        background: 'black',
        whiteSpace: 'pre-wrap',
      }}
    >
      {msg}
      {jsonMessage}
      {gqlQuery}
    </pre>
  );
};

const ErrorPage: FC<ErrorPageProps> = ({ error, resetErrorBoundary }) => {
  return (
    <Page background="secondary">
      <PageBody background="none">
        <Result
          align="center"
          status="error"
          label={Array.isArray(error) ? error[0].message : error.message}
          body="Page not found."
        >
          <Stack gutter="xsmall" justifyContent="center" slot="footer">
            <Button
              size="large"
              variant="secondary"
              onClick={resetErrorBoundary}
            >
              Reload Page
            </Button>
            <Button
              size="large"
              onClick={() => (document.location.pathname = '/')}
            >
              Go Home
            </Button>
          </Stack>
        </Result>
      </PageBody>
    </Page>
  );
};

const UncaughtErrorBoundary: FC<ChildrenProps> = ({ children }) => {
  const { isLocalError, isGlobalError, isXstateError } = useErrorTag();
  const managerMachineErrors = useManagerCtxSelector((ctx) => ctx.errors);
  const errors = !isLocalError ? managerMachineErrors : null;
  const { showBoundary } = useErrorBoundary();

  const handler = (e: ErrorEvent) => showBoundary(e);

  const removeErrorListener = () => {
    if (typeof window === 'undefined') {
      return;
    }

    window.removeEventListener('error', handler);
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (isLocalError || isXstateError) {
      removeErrorListener();

      return;
    }

    window.addEventListener('error', handler);

    return () => {
      removeErrorListener();
    };
  }, [isLocalError]);

  useEffect(() => {
    if (isGlobalError && errors && errors.length > 0) {
      showBoundary(errors);
    }
  }, [isGlobalError]);

  useEffect(() => {
    if (
      (isGlobalError || isLocalError || isXstateError) &&
      managerMachineErrors &&
      isProduction
    ) {
      managerMachineErrors.forEach((error) => {
        Sentry.captureException(error, {
          tags: {
            ...(isXstateError && { [ErrorTags.xstate]: isXstateError }),
            ...(isLocalError && { [ErrorTags.local]: isLocalError }),
            ...(isGlobalError && { [ErrorTags.global]: isGlobalError }),
          },
          extra: {
            source: 'UncaughtErrorBoundary',
            type: isGlobalError
              ? 'GlobalError'
              : isLocalError
                ? 'LocalError'
                : isXstateError
                  ? 'XstateError'
                  : 'UnknownError',
            error,
          },
        });
      });
    }
  }, [
    isGlobalError,
    isLocalError,
    isXstateError,
    managerMachineErrors,
    isProduction,
  ]);

  useEffect(() => {
    if (!isGlobalError || !errors || errors.length === 0) {
      return;
    }

    showBoundary(errors);
  }, [errors]);

  return <>{children}</>;
};

export const ErrorBoundary: FC<ChildrenProps> = ({ children }) => {
  const resetApp = () => {
    window.location.reload();
  };

  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorPage}
      onError={(e, i) => logError(e, i)}
      onReset={resetApp}
    >
      <UncaughtErrorBoundary>{children}</UncaughtErrorBoundary>
    </ReactErrorBoundary>
  );
};
